import type { TranslationTag } from '@ps/web-core-intl';
import React from 'react';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { type RootState } from '@swf/core/store';
import { EachWayPlaceTerms, ExpandablePanel, Icon, Title } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type {
  CompoundMarketTemplateProps,
  EventLevelTemplateId,
  MarketLevelTemplateId,
  MarketTemplateProps,
  SingleMarketTemplateProps
} from '@swf/sports/types/templates';
import { isCompoundMarketTemplateProps } from '@swf/sports/utils/isCompoundMarketTemplateProps';

import { TitleWithHighlight } from '../TitleWithHighlight/TitleWithHighlight';

import { MarketTemplate } from './MarketTemplate';
import {
  TXTWCL_Away_Team_First_Half_Over_Unde_ELL,
  TXTWCL_Away_Team_Over_Under,
  TXTWCL_Away_Team_Second_Half_Over_Und_ELL,
  TXTWCL_Away_Team_Total_Cards_in_a_Gam_ELL,
  TXTWCL_Away_Team_Total_Corners,
  TXTWCL_Both_Teams_to_Score_Markets,
  TXTWCL_Corners_Race,
  TXTWCL_First_Half_Over_Under_Goals,
  TXTWCL_Game_Lines,
  TXTWCL_Goalscorers,
  TXTWCL_Home_Team_First_Half_Over_Unde_ELL,
  TXTWCL_Home_Team_Over_Under,
  TXTWCL_Home_Team_Second_Half_Over_Und_ELL,
  TXTWCL_Home_Team_Total_Cards_in_a_Gam_ELL,
  TXTWCL_Home_Team_Total_Corners,
  TXTWCL_Over_Under_Goals,
  TXTWCL_Player_Assists,
  TXTWCL_Player_Made_Threes,
  TXTWCL_Player_Passing_TDs,
  TXTWCL_Player_Passing_Yds,
  TXTWCL_Player_Points,
  TXTWCL_Player_Rebounds,
  TXTWCL_Player_Receiving_Yds,
  TXTWCL_Player_Rushing_Yds,
  TXTWCL_Player_Total_Receptions,
  TXTWCL_Second_Half_Over_Under_Goals,
  TXTWCL_Set_1_Total_Games,
  TXTWCL_Set_2_Total_Games,
  TXTWCL_TD_Scorer,
  TXTWCL_Team_Total_Shots,
  TXTWCL_Team_Total_Shots_On_Target,
  TXTWCL_Total_Cards,
  TXTWCL_Total_Corners,
  TXTWCL_odds_boost
} from './MarketTemplatePanel.messages.json';
import styles from './MarketTemplatePanel.scss';
import MarketTitleWithIcons from './components/MarketTitleWithIcons/MarketTitleWithIcons';
import StarSubTermsAndConditions from './components/TermsAndConditions/TermsAndConditions';

export interface Props {
  isOpenInitially: boolean;
  highlight?: string;
  templateId: MarketLevelTemplateId | EventLevelTemplateId;
}

export const translationMap: Partial<Record<string, TranslationTag>> = {
  ISP_I18N_FIRST_HALF_OVER_UNDER_GOALS: TXTWCL_First_Half_Over_Under_Goals,
  ISP_I18N_SECOND_HALF_OVER_UNDER_GOALS: TXTWCL_Second_Half_Over_Under_Goals,
  ISP_I18N_GOALSCORERS: TXTWCL_Goalscorers,
  ISP_I18N_AWAY_TEAM_FIRST_HALF_OVER_UNDER: TXTWCL_Away_Team_First_Half_Over_Unde_ELL,
  ISP_I18N_AWAY_TEAM_SECOND_HALF_OVER_UNDER: TXTWCL_Away_Team_Second_Half_Over_Und_ELL,
  ISP_I18N_BOTH_TEAMS_TO_SCORE_MARKETS: TXTWCL_Both_Teams_to_Score_Markets,
  ISP_I18N_HOME_TEAM_FIRST_HALF_OVER_UNDER: TXTWCL_Home_Team_First_Half_Over_Unde_ELL,
  ISP_I18N_HOME_TEAM_SECOND_HALF_OVER_UNDER: TXTWCL_Home_Team_Second_Half_Over_Und_ELL,
  ISP_I18N_OVER_UNDER_GOALS: TXTWCL_Over_Under_Goals,
  ISP_I18N_HOME_TEAM_OVER_UNDER: TXTWCL_Home_Team_Over_Under,
  ISP_I18N_AWAY_TEAM_OVER_UNDER: TXTWCL_Away_Team_Over_Under,
  ISP_I18N_HOME_TEAM_TOTAL_CORNERS: TXTWCL_Home_Team_Total_Corners,
  ISP_I18N_AWAY_TEAM_TOTAL_CORNERS: TXTWCL_Away_Team_Total_Corners,
  ISP_I18N_HOME_TEAM_TOTAL_CARDS_IN_A_GAME: TXTWCL_Home_Team_Total_Cards_in_a_Gam_ELL,
  ISP_I18N_AWAY_TEAM_TOTAL_CARDS_IN_A_GAME: TXTWCL_Away_Team_Total_Cards_in_a_Gam_ELL,
  ISP_I18N_TEAM_TOTAL_SHOTS: TXTWCL_Team_Total_Shots,
  ISP_I18N_TEAM_TOTAL_SHOTS_ON_TARGET: TXTWCL_Team_Total_Shots_On_Target,
  ISP_I18N_CORNERS_RACE: TXTWCL_Corners_Race,
  ISP_I18N_TOTAL_CORNERS: TXTWCL_Total_Corners,
  ISP_I18N_TOTAL_CARDS: TXTWCL_Total_Cards,
  ISP_I18N_SET_1_TOTAL_GAMES: TXTWCL_Set_1_Total_Games,
  ISP_I18N_SET_2_TOTAL_GAMES: TXTWCL_Set_2_Total_Games,
  ISP_I18N_GAME_LINES: TXTWCL_Game_Lines,
  ISP_I18N_TOUCHDOWN_SCORERS: TXTWCL_TD_Scorer,
  ISP_I18N_PLAYER_TOTAL_RECEPTIONS: TXTWCL_Player_Total_Receptions,
  ISP_I18N_PLAYER_RECEIVING_YDS: TXTWCL_Player_Receiving_Yds,
  ISP_I18N_PLAYER_RUSHING_YDS: TXTWCL_Player_Rushing_Yds,
  ISP_I18N_PLAYER_PASSING_TDS: TXTWCL_Player_Passing_TDs,
  ISP_I18N_PLAYER_PASSING_YDS: TXTWCL_Player_Passing_Yds,
  ISP_I18N_PLAYER_MADE_THREES: TXTWCL_Player_Made_Threes,
  ISP_I18N_PLAYER_POINTS: TXTWCL_Player_Points,
  ISP_I18N_PLAYER_ASSISTS: TXTWCL_Player_Assists,
  ISP_I18N_PLAYER_REBOUNDS: TXTWCL_Player_Rebounds
};

const SingleMarketTemplatePanel = ({
  highlight,
  isOpenInitially,
  ...restProps
}: Props & SingleMarketTemplateProps) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const { marketName, placeTerms, isStarSubEnabled, isSgmEligible } =
    useSelector((state: RootState) => widgetSelect.market(state, restProps.marketId)) ?? {};
  const { formatMessage } = useRegulatoryIntl();

  const isOddsBoost = restProps.templateId === 'ODDS_BOOST';
  const { termsAndConditions } = restProps.templateConfig || {};

  let title: React.ReactNode = marketName;
  const titleString = marketName;

  if (highlight?.length && titleString) {
    title = <TitleWithHighlight title={titleString} partToHighlight={highlight} />;
  }

  if (isOddsBoost) {
    title = (
      <span className={styles.oddsboostTitle}>
        <Icon name="price-boost" label={formatMessage(TXTWCL_odds_boost)} />
        <span className={styles.oddsboostTitleText}>{title}</span>
      </span>
    );
  }

  if (isStarSubEnabled || isSgmEligible) {
    title = (
      <MarketTitleWithIcons
        title={title}
        isSgmEligible={isSgmEligible}
        isStarSubEnabled={isStarSubEnabled}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <ExpandablePanel
        isOpenInitially={isOpenInitially}
        title={<Title>{title}</Title>}
        isOddsBoost={isOddsBoost}
        panelName={marketName}
      >
        <>
          <EachWayPlaceTerms placeTerms={placeTerms} className={styles.eachWay} variant="long" />
          {termsAndConditions && (
            <StarSubTermsAndConditions termsAndConditions={termsAndConditions} />
          )}
          <MarketTemplate {...restProps} />
        </>
      </ExpandablePanel>
    </div>
  );
};

const CompoundMarketTemplatePanel = ({
  highlight,
  isOpenInitially,
  ...restProps
}: Props & CompoundMarketTemplateProps) => {
  const { config, marketIds } = restProps;
  const { termsAndConditions } = config;

  const { formatMessage } = useRegulatoryIntl();
  const { widgetSelect } = useEventMarketTemplatesWidget();

  const markets = useSelector((state: RootState) => widgetSelect.markets(state, marketIds));
  const isStarSubEnabled = Object.values(markets).some((market) => market?.isStarSubEnabled);
  const isSgmEligible = Object.values(markets).some((market) => market?.isSgmEligible);
  const titleTranslation = translationMap[config.title];

  let title: React.ReactNode = titleTranslation ? formatMessage(titleTranslation) : '';
  const panelName = title as string;

  if (highlight?.length) {
    title = <TitleWithHighlight title={panelName} partToHighlight={highlight} />;
  }

  if (isSgmEligible || isStarSubEnabled) {
    title = (
      <MarketTitleWithIcons
        title={title}
        isStarSubEnabled={isStarSubEnabled}
        isSgmEligible={isSgmEligible}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <ExpandablePanel
        isOpenInitially={isOpenInitially}
        title={<Title>{title}</Title>}
        panelName={panelName}
      >
        {termsAndConditions && (
          <StarSubTermsAndConditions termsAndConditions={termsAndConditions} />
        )}
        <MarketTemplate {...restProps} />
      </ExpandablePanel>
    </div>
  );
};

export const MarketTemplatePanel = (props: Props & MarketTemplateProps) =>
  isCompoundMarketTemplateProps(props) ? (
    <CompoundMarketTemplatePanel {...props} />
  ) : (
    <SingleMarketTemplatePanel {...props} />
  );
